import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Eye, 
  Calendar, 
  Clock, 
  Building2,
  User,
  Search,
  Filter,
  CheckSquare,
  Target,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Award,
  Activity,
  BarChart3,
  PieChart,
  ArrowUp,
  ArrowDown,
  Mail,
  Phone,
  MapPin,
  Crown,
  Shield,
  UserCheck,
  UserX,
  Briefcase,
  FolderOpen,
  AlertCircle,
  CheckCircle,
  XCircle,
  Zap,
  Star,
  MessageSquare,
  FileText,
  ChevronRight,
  Timer,
  Loader,
  Coffee,
  ThumbsUp,
  ThumbsDown,
  Flame,
  MoreHorizontal,
  ChevronDown,
  Hash,
  Sparkles,
  Brain,
  Heart,
  DollarSign,
  Trophy
} from 'lucide-react';

const UserDashboards = () => {
  const { currentUser } = useAuth();
  const { users = [], departments = [], teams = [], tasks = [], projects = [] } = useData();
  const navigate = useNavigate();
  
  // States
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [expandedUser, setExpandedUser] = useState(null);
  const [sortBy, setSortBy] = useState('performance');
  const [showOnlyProblematic, setShowOnlyProblematic] = useState(false);
  const [timeRange, setTimeRange] = useState('week');

  // פונקציה לחישוב סטטיסטיקות למשתמש
  const getUserStats = (userId) => {
    const userTasks = tasks.filter(task => 
      task.assigneeId === userId || 
      (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(userId))
    );
    
    const completedTasks = userTasks.filter(t => t.status === 'הושלם');
    const pendingTasks = userTasks.filter(t => t.status === 'ממתין לעבודה' || t.status === 'במסלול');
    const overdueTasks = userTasks.filter(t => 
      t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'הושלם'
    );
    
    const userProjects = projects.filter(project => 
      project.ownerId === userId || 
      (Array.isArray(project.memberIds) && project.memberIds.includes(userId))
    );
    
    const activeProjects = userProjects.filter(p => p.status === 'במסלול');
    const completedProjects = userProjects.filter(p => p.status === 'הושלם');
    const onHoldProjects = userProjects.filter(p => p.status === 'מושהה');
    
    const taskCompletionRate = userTasks.length > 0 
      ? Math.round((completedTasks.length / userTasks.length) * 100) 
      : 0;
    
    const onTimeRate = completedTasks.length > 0
      ? Math.round((completedTasks.filter(t => !t.wasOverdue).length / completedTasks.length) * 100)
      : 100;
    
    const performanceScore = Math.round((taskCompletionRate + onTimeRate) / 2);
    
    const lastActivity = [...userTasks, ...userProjects]
      .filter(item => item.updatedAt)
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0];
    
    // חישוב משימות שלא טופלו
    const untouchedTasks = userTasks.filter(t => 
      !t.startedAt && t.status === 'ממתין לעבודה'
    );
    
    // חישוב פרודוקטיביות
    const productivity = completedTasks.length > 0 
      ? Math.round((completedTasks.length / (new Date().getDate())) * 10) / 10
      : 0;
    
    return {
      tasks: {
        total: userTasks.length,
        completed: completedTasks.length,
        pending: pendingTasks.length,
        overdue: overdueTasks.length,
        untouched: untouchedTasks.length,
        completionRate: taskCompletionRate,
        highPriority: userTasks.filter(t => t.priority === 'high').length,
        mediumPriority: userTasks.filter(t => t.priority === 'medium').length,
        lowPriority: userTasks.filter(t => t.priority === 'low').length,
        list: userTasks
      },
      projects: {
        total: userProjects.length,
        active: activeProjects.length,
        completed: completedProjects.length,
        onHold: onHoldProjects.length,
        list: userProjects
      },
      performance: {
        score: performanceScore,
        onTimeRate,
        productivity,
        trend: performanceScore > 70 ? 'up' : performanceScore > 40 ? 'stable' : 'down'
      },
      lastActivity: lastActivity ? new Date(lastActivity.updatedAt) : null,
      hasProblems: overdueTasks.length > 0 || onHoldProjects.length > 0 || untouchedTasks.length > 3,
      untouchedProjects: activeProjects.filter(p => !p.lastProgress).length
    };
  };

  // פונקציה לקבלת תג תפקיד
  const getRoleInfo = (role) => {
    const roleConfig = {
      'org_admin': { 
        label: 'מנכ"ל', 
        icon: Crown, 
        bgColor: 'bg-gradient-to-br from-purple-100 to-purple-200',
        borderColor: 'border-purple-300',
        textColor: 'text-purple-700',
        badgeColor: 'bg-purple-500'
      },
      'deputy_ceo': { 
        label: 'סמנכ"ל', 
        icon: Shield,
        bgColor: 'bg-gradient-to-br from-blue-100 to-blue-200',
        borderColor: 'border-blue-300',
        textColor: 'text-blue-700',
        badgeColor: 'bg-blue-500'
      },
      'dept_manager': { 
        label: 'מנהל מחלקה', 
        icon: Briefcase,
        bgColor: 'bg-gradient-to-br from-emerald-100 to-emerald-200',
        borderColor: 'border-emerald-300',
        textColor: 'text-emerald-700',
        badgeColor: 'bg-emerald-500'
      },
      'team_manager': { 
        label: 'מנהל צוות', 
        icon: Users,
        bgColor: 'bg-gradient-to-br from-amber-100 to-amber-200',
        borderColor: 'border-amber-300',
        textColor: 'text-amber-700',
        badgeColor: 'bg-amber-500'
      },
      'employee': { 
        label: 'עובד', 
        icon: User,
        bgColor: 'bg-gradient-to-br from-sky-100 to-sky-200',
        borderColor: 'border-sky-300',
        textColor: 'text-sky-700',
        badgeColor: 'bg-sky-500'
      }
    };
    
    return roleConfig[role] || roleConfig['employee'];
  };

  // סינון משתמשים
  const getFilteredUsers = () => {
    let filtered = users.filter(user => user.id !== currentUser?.id);

    if (selectedRole !== 'all') {
      filtered = filtered.filter(user => user.role === selectedRole);
    }

    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterDepartment) {
      filtered = filtered.filter(user => user.departmentId === filterDepartment);
    }

    if (filterTeam) {
      filtered = filtered.filter(user => user.teamId === filterTeam);
    }

    if (showOnlyProblematic) {
      filtered = filtered.filter(user => {
        const stats = getUserStats(user.id);
        return stats.hasProblems || stats.performance.score < 50;
      });
    }

    filtered.sort((a, b) => {
      const statsA = getUserStats(a.id);
      const statsB = getUserStats(b.id);
      
      switch(sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'tasks':
          return statsB.tasks.total - statsA.tasks.total;
        case 'projects':
          return statsB.projects.total - statsA.projects.total;
        case 'performance':
          return statsB.performance.score - statsA.performance.score;
        default:
          return 0;
      }
    });

    return filtered;
  };

  const filteredUsers = getFilteredUsers();

  // חישוב סטטיסטיקות כלליות
  const getOverallStats = () => {
    const stats = {
      totalUsers: filteredUsers.length,
      activeUsers: 0,
      totalTasks: 0,
      completedTasks: 0,
      overdueTasks: 0,
      untouchedTasks: 0,
      totalProjects: 0,
      activeProjects: 0,
      avgPerformance: 0,
      problematicUsers: 0,
      topPerformers: 0
    };

    filteredUsers.forEach(user => {
      const userStats = getUserStats(user.id);
      
      stats.totalTasks += userStats.tasks.total;
      stats.completedTasks += userStats.tasks.completed;
      stats.overdueTasks += userStats.tasks.overdue;
      stats.untouchedTasks += userStats.tasks.untouched;
      stats.totalProjects += userStats.projects.total;
      stats.activeProjects += userStats.projects.active;
      stats.avgPerformance += userStats.performance.score;
      
      if (userStats.hasProblems) {
        stats.problematicUsers++;
      }
      if (userStats.performance.score >= 80) {
        stats.topPerformers++;
      }
      if (userStats.lastActivity && (new Date() - userStats.lastActivity) < 86400000) {
        stats.activeUsers++;
      }
    });

    stats.avgPerformance = filteredUsers.length > 0 
      ? Math.round(stats.avgPerformance / filteredUsers.length)
      : 0;

    return stats;
  };

  const overallStats = getOverallStats();

  const getDepartmentName = (departmentId) => {
    const dept = departments.find(d => d.id === departmentId);
    return dept?.name || 'ללא מחלקה';
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.name || 'ללא צוות';
  };

  const handleViewDashboard = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  // טאבים של תפקידים
  const roleTabs = [
    { id: 'all', label: 'כולם', icon: Users, count: users.filter(u => u.id !== currentUser?.id).length },
    { id: 'deputy_ceo', label: 'סמנכ"לים', icon: Shield, count: users.filter(u => u.role === 'deputy_ceo').length },
    { id: 'dept_manager', label: 'מנהלי מחלקות', icon: Briefcase, count: users.filter(u => u.role === 'dept_manager').length },
    { id: 'team_manager', label: 'מנהלי צוותים', icon: Users, count: users.filter(u => u.role === 'team_manager').length },
    { id: 'employee', label: 'עובדים', icon: User, count: users.filter(u => u.role === 'employee').length }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-lg border-b border-purple-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                מרכז ניהול עובדים
              </h1>
              <p className="text-sm text-gray-600 mt-1">ניטור ביצועים וניהול משימות בזמן אמת</p>
            </div>
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 rounded-lg hover:from-purple-200 hover:to-blue-200 transition-all text-sm font-medium">
                {new Date().toLocaleDateString('he-IL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border-2 border-purple-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-5 h-5 text-purple-500" />
              <span className="text-2xl font-bold text-purple-700">{overallStats.totalUsers}</span>
            </div>
            <p className="text-xs text-purple-600 font-medium">סה״כ עובדים</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border-2 border-green-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-5 h-5 text-green-500" />
              <span className="text-2xl font-bold text-green-700">{overallStats.activeUsers}</span>
            </div>
            <p className="text-xs text-green-600 font-medium">פעילים היום</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border-2 border-blue-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <CheckSquare className="w-5 h-5 text-blue-500" />
              <span className="text-2xl font-bold text-blue-700">{overallStats.totalTasks}</span>
            </div>
            <p className="text-xs text-blue-600 font-medium">משימות</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border-2 border-emerald-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span className="text-2xl font-bold text-emerald-700">{overallStats.completedTasks}</span>
            </div>
            <p className="text-xs text-emerald-600 font-medium">הושלמו</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border-2 border-amber-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-5 h-5 text-amber-500" />
              <span className="text-2xl font-bold text-amber-700">{overallStats.untouchedTasks}</span>
            </div>
            <p className="text-xs text-amber-600 font-medium">לא טופלו</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border-2 border-red-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span className="text-2xl font-bold text-red-700">{overallStats.overdueTasks}</span>
            </div>
            <p className="text-xs text-red-600 font-medium">באיחור</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border-2 border-indigo-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 text-indigo-500" />
              <span className="text-2xl font-bold text-indigo-700">{overallStats.avgPerformance}%</span>
            </div>
            <p className="text-xs text-indigo-600 font-medium">ביצועים ממוצע</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border-2 border-yellow-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Star className="w-5 h-5 text-yellow-500" />
              <span className="text-2xl font-bold text-yellow-700">{overallStats.topPerformers}</span>
            </div>
            <p className="text-xs text-yellow-600 font-medium">מצטיינים</p>
          </div>
        </div>

        {/* Filters & Tabs */}
        <div className="bg-white rounded-xl border-2 border-purple-200 p-4 mb-6 shadow-lg">
          {/* Role Tabs */}
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
            {roleTabs.map(tab => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setSelectedRole(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-2 ${
                    selectedRole === tab.id
                      ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg'
                      : 'bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 hover:from-purple-200 hover:to-blue-200'
                  }`}
                >
                  <TabIcon className="w-4 h-4" />
                  {tab.label}
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                    selectedRole === tab.id ? 'bg-white/30 text-white' : 'bg-purple-200 text-purple-700'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search & Filters */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="relative col-span-2">
              <Search className="absolute right-3 top-2.5 w-4 h-4 text-purple-400" />
              <input
                type="text"
                placeholder="חיפוש לפי שם או אימייל..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 bg-purple-50 border-2 border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              />
            </div>

            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              className="px-3 py-2 bg-blue-50 border-2 border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">כל המחלקות</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>

            <select
              value={filterTeam}
              onChange={(e) => setFilterTeam(e.target.value)}
              className="px-3 py-2 bg-green-50 border-2 border-green-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
            >
              <option value="">כל הצוותים</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 bg-amber-50 border-2 border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
            >
              <option value="performance">מיון לפי ביצועים</option>
              <option value="tasks">מיון לפי משימות</option>
              <option value="projects">מיון לפי פרויקטים</option>
              <option value="name">מיון לפי שם</option>
            </select>

            <label className="flex items-center gap-2 cursor-pointer bg-red-50 px-3 py-2 rounded-lg border-2 border-red-200 hover:bg-red-100 transition-all">
              <input
                type="checkbox"
                checked={showOnlyProblematic}
                onChange={(e) => setShowOnlyProblematic(e.target.checked)}
                className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
              />
              <Flame className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-700 font-medium">בעייתיים בלבד</span>
            </label>
          </div>
        </div>

        {/* Users Grid */}
        <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : ''}`}>
          {filteredUsers.map((user, index) => {
            const stats = getUserStats(user.id);
            const roleInfo = getRoleInfo(user.role);
            const RoleIcon = roleInfo.icon;
            const isExpanded = expandedUser === user.id;
            
            return (
              <div
                key={user.id}
                className={`bg-white rounded-xl border-2 ${stats.hasProblems ? 'border-red-300' : 'border-purple-200'} hover:shadow-xl transition-all duration-300 shadow-lg ${
                  isExpanded ? 'lg:col-span-2' : ''
                }`}
              >
                {/* Card Header */}
                <div className={`p-5 ${roleInfo.bgColor} border-b-2 ${roleInfo.borderColor}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl ${roleInfo.badgeColor} flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800 text-xl">{user.name}</h3>
                        <p className={`text-sm ${roleInfo.textColor} font-bold`}>{roleInfo.label}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {stats.performance.score >= 80 && (
                        <Trophy className="w-6 h-6 text-yellow-500" />
                      )}
                      {stats.hasProblems && (
                        <AlertCircle className="w-6 h-6 text-red-500 animate-pulse" />
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-3 py-1 bg-white/90 rounded-lg flex items-center gap-1 font-medium">
                      <Building2 className="w-3 h-3 text-purple-600" />
                      {getDepartmentName(user.departmentId)}
                    </span>
                    <span className="px-3 py-1 bg-white/90 rounded-lg flex items-center gap-1 font-medium">
                      <Users className="w-3 h-3 text-blue-600" />
                      {getTeamName(user.teamId)}
                    </span>
                  </div>
                </div>

                {/* Performance Score */}
                <div className="p-5">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-gray-700">ציון ביצועים כולל</span>
                      <span className={`text-3xl font-bold ${
                        stats.performance.score >= 80 ? 'text-green-600' :
                        stats.performance.score >= 60 ? 'text-blue-600' :
                        stats.performance.score >= 40 ? 'text-amber-600' :
                        'text-red-600'
                      }`}>
                        {stats.performance.score}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                      <div 
                        className={`h-3 rounded-full transition-all duration-500 ${
                          stats.performance.score >= 80 ? 'bg-gradient-to-r from-green-400 to-green-600' :
                          stats.performance.score >= 60 ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                          stats.performance.score >= 40 ? 'bg-gradient-to-r from-amber-400 to-amber-600' :
                          'bg-gradient-to-r from-red-400 to-red-600'
                        }`}
                        style={{ width: `${stats.performance.score}%` }}
                      />
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                      <CheckSquare className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-purple-700">{stats.tasks.total}</div>
                      <p className="text-xs text-purple-600 font-medium">משימות</p>
                    </div>
                    <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                      <FolderOpen className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-blue-700">{stats.projects.total}</div>
                      <p className="text-xs text-blue-600 font-medium">פרויקטים</p>
                    </div>
                    <div className="text-center p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                      <Activity className="w-5 h-5 text-green-600 mx-auto mb-1" />
                      <div className="text-2xl font-bold text-green-700">{stats.performance.productivity}</div>
                      <p className="text-xs text-green-600 font-medium">משימות/יום</p>
                    </div>
                  </div>

                  {/* Tasks Breakdown */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm bg-green-50 p-2 rounded-lg">
                      <span className="text-green-700 font-medium">משימות שהושלמו</span>
                      <span className="font-bold text-green-800 text-lg">{stats.tasks.completed}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm bg-blue-50 p-2 rounded-lg">
                      <span className="text-blue-700 font-medium">משימות בטיפול</span>
                      <span className="font-bold text-blue-800 text-lg">{stats.tasks.pending}</span>
                    </div>
                    {stats.tasks.untouched > 0 && (
                      <div className="flex items-center justify-between text-sm bg-amber-50 p-2 rounded-lg">
                        <span className="text-amber-700 font-medium">משימות שלא טופלו</span>
                        <span className="font-bold text-amber-800 text-lg">{stats.tasks.untouched}</span>
                      </div>
                    )}
                    {stats.tasks.overdue > 0 && (
                      <div className="flex items-center justify-between text-sm bg-red-50 p-2 rounded-lg">
                        <span className="text-red-700 font-medium">משימות באיחור</span>
                        <span className="font-bold text-red-800 text-lg animate-pulse">{stats.tasks.overdue}</span>
                      </div>
                    )}
                  </div>

                  {/* Alerts */}
                  {stats.hasProblems && (
                    <div className="p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border-2 border-red-200 mb-4">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0 animate-pulse" />
                        <div className="text-sm">
                          <p className="font-bold text-red-900 mb-1">דורש התערבות מיידית!</p>
                          {stats.tasks.overdue > 0 && (
                            <p className="text-red-700 font-medium">• {stats.tasks.overdue} משימות באיחור</p>
                          )}
                          {stats.tasks.untouched > 3 && (
                            <p className="text-orange-700 font-medium">• {stats.tasks.untouched} משימות לא טופלו</p>
                          )}
                          {stats.projects.onHold > 0 && (
                            <p className="text-amber-700 font-medium">• {stats.projects.onHold} פרויקטים מושהים</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Button */}
                  <button
                    onClick={() => handleViewDashboard(user.id)}
                    className="w-full py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all text-sm font-bold shadow-lg flex items-center justify-center gap-2"
                  >
                    {isExpanded ? 'סגור פרטים' : 'הצג פרטים מלאים'}
                    <Eye className="w-4 h-4" />
                  </button>

                  {/* Expanded View */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t-2 border-purple-200 space-y-4">
                      <div>
                        <h4 className="text-sm font-bold text-gray-800 mb-2">משימות שלא טופלו</h4>
                        {stats.tasks.untouched > 0 ? (
                          <div className="space-y-2">
                            {stats.tasks.list
                              .filter(t => !t.startedAt && t.status === 'ממתין לעבודה')
                              .slice(0, 3)
                              .map(task => (
                                <div key={task.id} className="p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                                  <p className="text-sm font-bold text-gray-800">{task.title}</p>
                                  <p className="text-xs text-amber-700 mt-1">
                                    נוצר ב: {new Date(task.createdAt).toLocaleDateString('he-IL')}
                                  </p>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">כל המשימות בטיפול</p>
                        )}
                      </div>

                      <div>
                        <h4 className="text-sm font-bold text-gray-800 mb-2">פרויקטים פעילים</h4>
                        {stats.projects.active > 0 ? (
                          <div className="space-y-2">
                            {stats.projects.list
                              .filter(p => p.status === 'במסלול')
                              .slice(0, 3)
                              .map(project => (
                                <div key={project.id} className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                                  <div className="flex items-center justify-between">
                                    <p className="text-sm font-bold text-gray-800">{project.name}</p>
                                    <span className="text-xs text-blue-700 font-bold">{project.progress || 0}%</span>
                                  </div>
                                  <div className="mt-2 w-full bg-blue-100 rounded-full h-2">
                                    <div 
                                      className="h-2 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full"
                                      style={{ width: `${project.progress || 0}%` }}
                                    />
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">אין פרויקטים פעילים</p>
                        )}
                      </div>

                      <div className="flex gap-3">
                        <div className="flex-1 p-3 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                          <Mail className="w-4 h-4 text-purple-600 mb-1" />
                          <p className="text-xs text-purple-600 font-medium">אימייל</p>
                          <p className="text-sm font-bold text-gray-800">{user.email}</p>
                        </div>
                        {user.phone && (
                          <div className="flex-1 p-3 bg-gradient-to-br from-green-50 to-blue-50 rounded-lg border border-green-200">
                            <Phone className="w-4 h-4 text-green-600 mb-1" />
                            <p className="text-xs text-green-600 font-medium">טלפון</p>
                            <p className="text-sm font-bold text-gray-800">{user.phone}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredUsers.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl border-2 border-purple-200 shadow-lg">
            <UserX className="w-16 h-16 text-purple-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-800 mb-2">לא נמצאו עובדים</h3>
            <p className="text-gray-600">נסה לשנות את הפילטרים או החיפוש</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboards;